import { useQuery } from "@tanstack/react-query";
import { useAtom } from "jotai";
import { useTranslation } from "react-i18next";
import { getApiUrl } from "@/utils/api";
import { useAuth } from "@/hooks/useAuth";
import { companyAtom } from "@/store/company";
import { Company } from "@/types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { communicationMenu } from "@/components/constants/menus";
import { useEffect } from "react";

export interface CommunicationMenuItem {
  name: string;
  label: string;
}

interface CommunicationSideMenuProps {
  onCategorySelect?: (category: CommunicationMenuItem) => void;
  selectedCategoryId?: string;
}

export default function CommunicationSideMenu({
  onCategorySelect,
  selectedCategoryId,
}: CommunicationSideMenuProps) {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [company, setCompany] = useAtom(companyAtom);

  // Fetch companies
  const { data: companiesData = { results: [] } } = useQuery<{
    results: Company[];
  }>({
    queryKey: ["companies"],
    queryFn: async () => {
      try {
        const url = getApiUrl("companies/");
        const response = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch companies: ${response.status}`);
        }

        const data = await response.json();
        return data;
      } catch (error) {
        console.error("Error fetching companies:", error);
        return [];
      }
    },
    enabled: !!token,
  });

  // Handle company change
  const handleCompanyChange = (companyId: string) => {
    const selectedCompany = companiesData?.results?.find(
      (c) => c.id.toString() === companyId,
    );
    if (selectedCompany) {
      setCompany(selectedCompany);
    }
  };

  useEffect(() => {
    // Auto-select first category if none is selected
    onCategorySelect?.(communicationMenu[0]);
  }, []);

  useEffect(() => {
    // Auto-select first company if none is selected and companies are available
    if (!company && companiesData?.results?.length > 0) {
      setCompany(companiesData.results[0]);
    }
  }, [company, companiesData?.results, setCompany]);

  // Handle category selection
  const handleCategorySelect = (category: CommunicationMenuItem) => {
    if (onCategorySelect) {
      onCategorySelect(category);
    }
  };

  return (
    <div className="bg-[#f5f5f5] border-r h-full px-2 py-4 w-[189px] animate-slide-in-left-1/2s">
      {/* Company Selector */}
      <div className="mb-6">
        {/* <label className="text-2xl font-semibold">{t("companies.title")}</label> */}
        <Select
          value={company?.id?.toString()}
          onValueChange={handleCompanyChange}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder={t("companies.select_placeholder")} />
          </SelectTrigger>
          <SelectContent>
            {companiesData?.results?.map((company) => (
              <SelectItem key={company.id} value={company.id.toString()}>
                {company.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Categories by Scope */}
      {company ? (
        <div className="space-y-4">
          <div className="space-y-1">
            {communicationMenu.map((category) => (
              <div key={category.name} className="flex items-center space-x-2">
                <Button
                  variant="ghost"
                  className={`w-full justify-start text-sm capitalize ${
                    selectedCategoryId === category.name
                      ? "bg-accent text-accent-foreground"
                      : "hover:bg-muted/50"
                  }`}
                  onClick={() => handleCategorySelect(category)}
                >
                  {t(category.name)}
                </Button>
              </div>
            ))}
          </div>

          {/* Add Category Button */}
          {/* <Button variant="outline" size="sm" className="w-full mt-4 text-xs">
            <Plus className="h-4 w-4 mr-1" />
            {t("category.add")}
          </Button> */}
        </div>
      ) : (
        <div className="text-sm text-muted-foreground text-center py-4">
          {t("companies.select_placeholder")}
        </div>
      )}
    </div>
  );
}
